import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Button } from '../ui';
import Badge from '../ui/Badge';
import ProgressBar from '../raffle/ProgressBar';
import CountdownTimer from '../raffle/CountdownTimer';
import { Section, EmptyState } from './ActiveEntriesSection';

/**
 * SellerSection - Shows seller's active competitions and pending handovers
 *
 * For users who have created competitions, displays their selling activity.
 */
export default function SellerSection({
  raffles,
  pendingHandovers,
  hasStripeConnect,
  className,
}) {
  // Active raffle (only 1 allowed at a time)
  const activeRaffle = raffles.find(r => r.status === 'active');

  // Pending handovers where user is seller
  const sellerHandovers = pendingHandovers.filter(h => h.sellerId && h.status === 'pending');

  // If not a seller and no Stripe Connect, show seller CTA
  if (!hasStripeConnect && raffles.length === 0) {
    return (
      <Section title="Selling" className={className}>
        <SellerCTA />
      </Section>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Active Competition */}
      <Section title="Your Active Competition">
        {activeRaffle ? (
          <ActiveRaffleCard raffle={activeRaffle} />
        ) : (
          <EmptyState
            icon={PlusIcon}
            title="No active competition"
            description="You can list one item at a time. Create a competition to start selling!"
            actionLabel="Create Competition"
            actionHref="/create"
          />
        )}
      </Section>

      {/* Pending Handovers (if any) */}
      {sellerHandovers.length > 0 && (
        <Section
          title="Pending Handovers"
          count={sellerHandovers.length}
          actionLabel="View All"
          actionHref="/my-competitions?filter=completed"
        >
          <div className="space-y-3">
            {sellerHandovers.slice(0, 3).map((handover) => (
              <HandoverCard key={handover.id} handover={handover} />
            ))}
          </div>
        </Section>
      )}

      {/* Recent Completed (quick earnings view) */}
      {raffles.filter(r => r.status === 'completed').length > 0 && (
        <Section
          title="Recent Sales"
          actionLabel="View All"
          actionHref="/my-competitions"
        >
          <div className="space-y-3">
            {raffles
              .filter(r => r.status === 'completed')
              .slice(0, 3)
              .map((raffle) => (
                <CompletedRaffleCard key={raffle.id} raffle={raffle} />
              ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function ActiveRaffleCard({ raffle }) {
  const photo = raffle.photos?.[0];
  const progress = raffle.ticketQuantity > 0
    ? (raffle.ticketsSold / raffle.ticketQuantity) * 100
    : 0;
  const revenue = (raffle.ticketsSold || 0) * (raffle.ticketPrice || 0);

  return (
    <Link
      to={`/competition/${raffle.id}`}
      className="group block bg-midnight/50 rounded-xl border border-primary/20 hover:border-primary/40 transition-all overflow-hidden"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Photo */}
        <div className="sm:w-40 h-32 sm:h-auto overflow-hidden bg-midnight-lighter shrink-0">
          {photo && (
            <img
              src={photo}
              alt={raffle.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h4 className="font-semibold text-cream group-hover:text-primary transition-colors">
                {raffle.title}
              </h4>
              <p className="text-sm text-cream/50 mt-0.5">
                {raffle.pickupArea}
              </p>
            </div>
            <Badge variant="active" size="sm">Live</Badge>
          </div>

          {/* Progress */}
          <div className="mb-3">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-cream/60">
                {raffle.ticketsSold || 0} / {raffle.ticketQuantity} entries
              </span>
              <span className="text-cream/60">{Math.round(progress)}%</span>
            </div>
            <ProgressBar
              soldCount={raffle.ticketsSold || 0}
              totalCount={raffle.ticketQuantity}
              threshold={raffle.minimumThreshold}
              showThreshold
            />
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <span className="text-cream/60">
                Revenue: <span className="text-primary font-mono font-semibold">{formatCurrency(revenue)}</span>
              </span>
            </div>
            <CountdownTimer endsAt={raffle.endsAt} compact className="text-cream/60" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function HandoverCard({ handover }) {
  const needsAction = !handover.sellerConfirmed;

  return (
    <Link
      to={`/competition/${handover.raffleId}`}
      className={cn(
        'flex items-center justify-between gap-4 rounded-xl p-4 border transition-all',
        needsAction
          ? 'bg-warning/5 border-warning/30 hover:border-warning/50'
          : 'bg-midnight-lighter/50 border-transparent hover:border-primary/20'
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm text-cream/60">Handover pending</p>
        <p className="font-medium text-cream truncate">
          Raffle #{handover.raffleId?.slice(0, 8)}
        </p>
      </div>

      {needsAction ? (
        <span className="flex items-center gap-1 text-xs font-medium text-warning shrink-0">
          <AlertIcon className="w-4 h-4" />
          Confirm handover
        </span>
      ) : (
        <Badge variant="primary" size="sm">Waiting for buyer</Badge>
      )}
    </Link>
  );
}

function CompletedRaffleCard({ raffle }) {
  const revenue = (raffle.ticketsSold || 0) * (raffle.ticketPrice || 0);

  return (
    <Link
      to={`/competition/${raffle.id}`}
      className="flex items-center gap-4 bg-midnight-lighter/50 hover:bg-midnight-lighter rounded-xl p-3 border border-transparent hover:border-primary/20 transition-all"
    >
      <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-midnight-lighter">
        {raffle.photos?.[0] && (
          <img
            src={raffle.photos[0]}
            alt={raffle.title}
            className="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-cream truncate">{raffle.title}</h4>
        <p className="text-sm text-cream/50">
          {raffle.ticketsSold} entries sold
        </p>
      </div>

      <div className="text-right shrink-0">
        <p className="text-lg font-mono font-semibold text-success">
          {formatCurrency(revenue)}
        </p>
        <Badge variant="success" size="sm">Completed</Badge>
      </div>
    </Link>
  );
}

function SellerCTA() {
  return (
    <div className="text-center py-8">
      <div className="mx-auto w-16 h-16 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mb-4">
        <CurrencyIcon className="w-8 h-8 text-accent" />
      </div>
      <h4 className="font-display font-semibold text-lg text-cream mb-2">
        Start Selling
      </h4>
      <p className="text-cream/50 text-sm mb-6 max-w-xs mx-auto">
        Turn your items into cash. Set up payments and create your first competition.
      </p>
      <Link to="/seller/onboarding">
        <Button variant="accent">Get Started</Button>
      </Link>
    </div>
  );
}

// Helper function
function formatCurrency(cents) {
  if (!cents || cents === 0) return '€0';
  return `€${(cents / 100).toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// Icons
function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function CurrencyIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function AlertIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}
